// Copyright (c) 2026 Michael F. Kamprath
//
// number-plaque.js -- Themed plaque + numeral display for counted
// objects in Learn mode.
//
// Each theme supplies a `numberPlaqueConfig` block with:
//   image        relative path to the plaque background webp
//   imageSize    { w, h }       design-coord size of the plaque sprite
//   centerPoint  { x, y }       plaque-center offset on the sprite
//                               (measured from the sprite's top-left)
//   numberFrame  { x, y, w, h } numeral rectangle inside the plaque image
//   fontColor    hex string     numeral color (defaults to black)
//   maxHeight    optional       clamp plaque height, scaling uniformly
//   animator     { type, ... }  entrance animation spec (Increment C)
//
// This module renders the static plaque + numeral.  Entrance animation
// is layered on top in Increment C (plaque-animators.js).
//
// Coordinate system: createNumberPlaque returns a PIXI Container whose
// visual center is at (0, 0) in its local space.  Callers position the
// container at `centerPoint` relative to the sprite container so the
// plaque's center sits exactly where the theme config wants it.
//
// Numeral rendering supports the five scripts declared in
// src/data/numerals.json (Arabic, Chinese characters, Japanese
// hiragana, Sino-Korean, Native Korean).  formatNumeral here
// delegates to ../numerals.js so the script table lives in one
// place.  CJK glyphs need the Noto Sans CJK font stack to be in
// the system font fallback chain or in the loaded webfonts -- see
// src/main.css for the @font-face declarations.

import * as PIXI from 'pixi.js';
import {
  formatNumeral as formatNumeralFromTable,
  getFontStackForScript,
} from '../numerals.js';

/**
 * Compute the uniform scale applied when `maxHeight` clamps the plaque.
 * Returns 1 when no clamping applies.
 *
 * @param {object} cfg  A theme's numberPlaqueConfig block.
 * @returns {number}
 */
export function plaqueDisplayScale(cfg) {
  if (!cfg?.maxHeight || !cfg?.imageSize?.h) return 1;
  if (cfg.maxHeight >= cfg.imageSize.h) return 1;
  return cfg.maxHeight / cfg.imageSize.h;
}

/**
 * Compute the numeral's offset from the plaque sprite's visual center,
 * already scaled by any active maxHeight clamp.
 *
 * numberFrame is expressed relative to the plaque image's top-left.
 * The plaque sprite is anchored at its center (0.5, 0.5), so the
 * offset equals (numberFrame.center - imageSize.center) * scale.
 *
 * @param {object} cfg
 * @returns {{ x: number, y: number }}
 */
export function computeNumeralOffset(cfg) {
  if (!cfg?.imageSize || !cfg?.numberFrame) return { x: 0, y: 0 };
  const s = plaqueDisplayScale(cfg);
  const numCx = cfg.numberFrame.x + cfg.numberFrame.w / 2;
  const numCy = cfg.numberFrame.y + cfg.numberFrame.h / 2;
  const imgCx = cfg.imageSize.w / 2;
  const imgCy = cfg.imageSize.h / 2;
  return {
    x: (numCx - imgCx) * s,
    y: (numCy - imgCy) * s,
  };
}

/**
 * Compute the numeral's font size.  Legacy iOS used a 0.80 scale factor
 * of the numberFrame height (kFontScaleFactor).  Clamped by maxHeight.
 *
 * @param {object} cfg
 * @returns {number}
 */
export function computeNumeralFontSize(cfg) {
  const h = cfg?.numberFrame?.h;
  if (!h) return 0;
  return h * 0.80 * plaqueDisplayScale(cfg);
}

/**
 * Measure the rendered glyph for a digit string and compute how far
 * the text's anchor (0.5, 0.5) must be shifted vertically so the
 * VISIBLE glyph -- not the font's bounding box -- is centered in
 * the numberFrame.
 *
 * Context: PIXI.Text with anchor (0.5, 0.5) centers the text's
 * BOUNDING BOX at (text.x, text.y).  The bounding box height is
 * based on the font's ascender + descender metrics and typically
 * extends well above the cap-line (to make room for accents)
 * and below the baseline.  For digits -- which sit between
 * baseline and cap-height -- that makes the visible glyph appear
 * shifted toward the top of the frame.
 *
 * The legacy iOS app solved this by measuring the text's actual
 * size and drawing it into a sub-rect sized to the measured
 * height (InternationalNumberDisplayLayer.m:71-92).  We replicate
 * that by using the browser's Canvas 2D measureText API, which
 * exposes both the glyph's actual bounding box
 * (`actualBoundingBoxAscent/Descent`) and the font's line-height
 * box (`fontBoundingBoxAscent/Descent`).
 *
 * Formula (y grows downward):
 *   bboxCenter  = (fontDescent - fontAscent) / 2
 *   glyphCenter = (visibleDescent - visibleAscent) / 2
 *   shift       = bboxCenter - glyphCenter
 *
 * Apply the returned shift to `text.y` to move the VISIBLE glyph
 * to where the BOUNDING BOX center used to sit.
 *
 * Returns 0 in environments without Canvas (jsdom tests, Node),
 * so the behaviour falls back to bounding-box centering.  In a
 * real browser on a font with symmetric ascender/descender the
 * shift is typically a few pixels.
 *
 * @param {object} opts
 * @param {string} opts.sample        The text whose glyph will be measured.
 * @param {number} opts.fontSize      Font size in px.
 * @param {string} opts.fontFamily    Font family string.
 * @param {string|number} [opts.fontWeight]
 * @returns {number}  Pixels to add to text.y (may be negative).
 */
export function computeGlyphCenteringYShift({
  sample,
  fontSize,
  fontFamily,
  fontWeight = '700',
}) {
  if (!sample || !Number.isFinite(fontSize) || fontSize <= 0) return 0;
  if (typeof document === 'undefined' || typeof document.createElement !== 'function') {
    return 0;
  }
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx) return 0;
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    const m = ctx.measureText(sample);
    // Safari/Chromium/Firefox all expose these properties in modern
    // versions.  If any are missing we abort and fall back to bbox
    // centering (shift=0).
    const fa = m.fontBoundingBoxAscent;
    const fd = m.fontBoundingBoxDescent;
    const va = m.actualBoundingBoxAscent;
    const vd = m.actualBoundingBoxDescent;
    if (![fa, fd, va, vd].every((n) => Number.isFinite(n))) return 0;
    const bboxCenter = (fd - fa) / 2;
    const glyphCenter = (vd - va) / 2;
    return bboxCenter - glyphCenter;
  } catch {
    return 0;
  }
}

/**
 * Shrink `candidateFontSize` if needed so that `sample` rendered at that
 * size doesn't overflow `maxWidth`.  Returns the largest font size that
 * fits inside maxWidth, capped at the candidate (we never UPSIZE --
 * this is a defensive scale-down only).
 *
 * Why: `computeNumeralFontSize` and choice-plaque's `numeralFontSize`
 * both size the font from `numberFrame.h` only (legacy iOS
 * kFontScaleFactor pattern, sized for narrow arabic / chinese digits).
 * Japanese hiragana numerals are MUCH wider than that assumption: 'じ
 * ゅうきゅう' (19) is five glyphs vs '19' / '十九' which are one or
 * two.  Without a width-fit pass the hiragana glyph string overflows
 * the plaque shield.  This helper measures the rendered width via
 * Canvas2D and proportionally shrinks the font when it doesn't fit.
 *
 * Width scales linearly with font size, so the largest fitting size
 * is `candidateFontSize * (available / measuredWidth)`.  Padded slightly
 * (default 4px) so the glyph doesn't kiss the edge of the frame.
 *
 * Returns `candidateFontSize` unchanged in environments without
 * Canvas (jsdom tests, Node), so behaviour falls back to height-only
 * sizing -- correct fallback for tests that assert the legacy size.
 *
 * @param {object} opts
 * @param {string} opts.sample            The text whose width is measured.
 * @param {number} opts.candidateFontSize Desired font size (height-derived).
 * @param {number} opts.maxWidth          Maximum allowed text width in px.
 * @param {string} opts.fontFamily        Font family string.
 * @param {string|number} [opts.fontWeight]  Defaults '700' (bold).
 * @param {number} [opts.padding]         Safety margin in px (default 4).
 * @returns {number}  Final font size, <= candidateFontSize.
 */
export function fitFontSizeToWidth({
  sample,
  candidateFontSize,
  maxWidth,
  fontFamily,
  fontWeight = '700',
  padding = 4,
}) {
  if (!sample || !Number.isFinite(candidateFontSize) || candidateFontSize <= 0) {
    return candidateFontSize;
  }
  if (!Number.isFinite(maxWidth) || maxWidth <= 0) {
    return candidateFontSize;
  }
  if (typeof document === 'undefined' || typeof document.createElement !== 'function') {
    return candidateFontSize;
  }
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx) return candidateFontSize;
    ctx.font = `${fontWeight} ${candidateFontSize}px ${fontFamily}`;
    const measuredWidth = ctx.measureText(sample).width;
    if (!Number.isFinite(measuredWidth) || measuredWidth <= 0) return candidateFontSize;
    const available = Math.max(1, maxWidth - padding);
    if (measuredWidth <= available) return candidateFontSize;
    return Math.max(1, candidateFontSize * (available / measuredWidth));
  } catch {
    return candidateFontSize;
  }
}

/**
 * Format a numeric value for the requested script.  Delegates to the
 * numerals.json glyph table via ../numerals.js.  Throws on unknown
 * scripts and out-of-range values so callers don't silently render
 * the wrong thing.
 *
 * @param {number} value
 * @param {string} [numeralScript]
 * @returns {string}
 */
export function formatNumeral(value, numeralScript = 'arabic') {
  return formatNumeralFromTable(value, numeralScript);
}

/**
 * Resolve a plaque image path to a PIXI Texture using the pre-loaded
 * texture map when possible.  Falls back to Assets.get and finally to
 * PIXI.Texture.EMPTY so rendering never crashes on a missing asset.
 *
 * @param {string} imagePath
 * @param {Map|Object|null} textureMap
 * @returns {PIXI.Texture}
 */
export function resolvePlaqueTexture(imagePath, textureMap) {
  if (!imagePath) return PIXI.Texture.EMPTY ?? null;
  if (textureMap) {
    const tex = (typeof textureMap.get === 'function')
      ? textureMap.get(imagePath)
      : textureMap[imagePath];
    if (tex) return tex;
  }
  const alias = imagePath.startsWith('/') ? imagePath : '/' + imagePath;
  return (PIXI.Assets && PIXI.Assets.get(alias)) || PIXI.Texture.EMPTY || null;
}

/**
 * Preload every theme's plaque background texture.  Returns a Map
 * keyed by the raw (unprefixed) image path, consistent with the
 * textureMap convention used by idle.js and tap.js.
 *
 * @param {object} themes  Parsed themes.json root object.
 * @returns {Promise<Map<string, PIXI.Texture>>}
 */
export async function preloadPlaqueTextures(themes) {
  const map = new Map();
  const { Assets } = PIXI;
  if (!Assets) return map;

  const paths = new Set();
  for (const theme of Object.values(themes || {})) {
    const p = theme?.numberPlaqueConfig?.image;
    if (p) paths.add(p);
  }

  for (const p of paths) {
    const alias = '/' + p;
    const cached = Assets.get(alias);
    if (cached) {
      map.set(p, cached);
      continue;
    }
    try {
      Assets.add({ alias, src: alias });
    } catch {
      /* already registered -- non-fatal */
    }
    try {
      const tex = await Assets.load(alias);
      if (tex) map.set(p, tex);
    } catch (err) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(`[number-plaque] failed to load ${alias}:`, err);
      }
    }
  }
  return map;
}

/**
 * Create a plaque Container displaying `value` using `theme`'s plaque
 * config.  The returned container's visual center is at (0, 0) in its
 * own coordinate space, so the caller positions it at the theme's
 * centerPoint.
 *
 * The container exposes:
 *   container._plaqueSprite  the background sprite
 *   container._plaqueText    the numeral Text
 *   container._plaqueConfig  reference back to the config (for animators)
 *
 * @param {object} opts
 * @param {object} opts.theme         Theme entry with numberPlaqueConfig.
 * @param {number} opts.value         Count to display.
 * @param {Map|Object} [opts.textureMap]   Pre-loaded textures.
 * @param {string} [opts.numeralScript]    Numeral script key from numerals.json.
 * @param {string} [opts.fontFamily]       Override the default script-aware font stack.
 * @returns {PIXI.Container}
 */
export function createNumberPlaque({
  theme,
  value,
  textureMap = null,
  numeralScript = 'arabic',
  fontFamily = getFontStackForScript(numeralScript),
}) {
  const cfg = theme?.numberPlaqueConfig;
  if (!cfg) {
    throw new Error(
      `createNumberPlaque: theme '${theme?.label ?? '?'}' has no numberPlaqueConfig`,
    );
  }

  const scale = plaqueDisplayScale(cfg);
  const displayW = cfg.imageSize.w * scale;
  const displayH = cfg.imageSize.h * scale;

  const container = new PIXI.Container();
  container._plaqueConfig = cfg;

  // Plaque background sprite
  const texture = resolvePlaqueTexture(cfg.image, textureMap);
  const sprite = new PIXI.Sprite(texture);
  sprite.anchor.set(0.5, 0.5);
  sprite.width = displayW;
  sprite.height = displayH;
  container.addChild(sprite);
  container._plaqueSprite = sprite;

  // Numeral
  const sample = formatNumeral(value, numeralScript);
  // Height-derived candidate; shrunk to fit width if needed (Japanese
  // hiragana like 'じゅうきゅう' overflows the arabic-sized frame).
  const candidateFontSize = computeNumeralFontSize(cfg);
  const maxTextWidth = (cfg?.numberFrame?.w ?? 0) * plaqueDisplayScale(cfg);
  const fontSize = fitFontSizeToWidth({
    sample,
    candidateFontSize,
    maxWidth: maxTextWidth,
    fontFamily,
    fontWeight: '700',
  });
  const text = new PIXI.Text({
    text: sample,
    style: {
      fontFamily,
      fontSize,
      fill: cfg.fontColor || '#000000',
      fontWeight: '700',
      align: 'center',
    },
  });
  // PIXI v8: Text has .anchor with .set().
  if (text.anchor && typeof text.anchor.set === 'function') {
    text.anchor.set(0.5, 0.5);
  }
  const offset = computeNumeralOffset(cfg);
  // Shift y so the VISIBLE glyph (not the font's line-height box)
  // is centered in the numberFrame.  See
  // computeGlyphCenteringYShift for the rationale.  Zero shift in
  // test environments without Canvas -- the tests assert bbox
  // centering, which is the correct fallback.
  const yShift = computeGlyphCenteringYShift({
    sample, fontSize, fontFamily, fontWeight: '700',
  });
  text.x = offset.x;
  text.y = offset.y + yShift;
  container.addChild(text);
  container._plaqueText = text;

  return container;
}

/**
 * Update the numeral displayed by an existing plaque container.
 * Useful when a counter increments but the plaque stays attached.
 *
 * @param {PIXI.Container} plaqueContainer
 * @param {number} value
 * @param {string} [numeralScript]
 */
export function setPlaqueValue(plaqueContainer, value, numeralScript = 'arabic') {
  const text = plaqueContainer?._plaqueText;
  if (!text) return;
  const next = formatNumeral(value, numeralScript);
  // Support both PIXI v8 (setter) and v7 (text property getter) interfaces.
  if ('text' in text) text.text = next;
}
